import {
	CalendarCheck,
	Eye,
	Flag,
	Gauge,
	ListChecks,
	MessageCircle,
	PenLine,
	Scale,
	Send,
} from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { useKpiMetrics } from '@/api/queries/kpi-metrics-queries'
import { AcoesConsolidadasDashboard } from '@/components/administration/kpi-metrics/acoes-consolidadas-dashboard'
import { CalibracaoDashboard } from '@/components/administration/kpi-metrics/calibracao-dashboard'
import { CartaoAmareloDashboard } from '@/components/administration/kpi-metrics/cartao-amarelo-dashboard'
import { DdsDashboard } from '@/components/administration/kpi-metrics/dds-dashboard'
import { DirecionadaDashboard } from '@/components/administration/kpi-metrics/direcionada-dashboard'
import { FreeTextDashboard } from '@/components/administration/kpi-metrics/free-text-dashboard'
import { InteracaoDashboard } from '@/components/administration/kpi-metrics/interacao-dashboard'
import { KpiMetricsFilterBar } from '@/components/administration/kpi-metrics/kpi-metrics-filter-bar'
import { KpiMetricsDashboardLoading } from '@/components/administration/kpi-metrics/shared/kpi-metrics-dashboard-loading'
import { VerEAgirDashboard } from '@/components/administration/kpi-metrics/ver-e-agir-dashboard'
import { CardButton } from '@/components/card-button'
import { AdminCardPage } from '@/components/commons'
import { hasKpiMetricsTabAccess } from '@/constants/permissions'
import { ROUTES } from '@/constants/routes'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { useAuthStore } from '@/store'
import { useKpiMetricsFilterStore } from '@/store/kpi-metrics-filter-store'
import type { KpiMetricsParams } from '@/types'

type KpiMetricsTab =
	| 'ver-e-agir'
	| 'acoes-consolidadas'
	| 'interacao'
	| 'calibracao'
	| 'direcionada'
	| 'resposta-livre'
	| 'cartao-amarelo'
	| 'dds'

const OWN_FETCH_TABS: KpiMetricsTab[] = [
	'ver-e-agir',
	'interacao',
	'direcionada',
	'resposta-livre',
	'dds',
]

export const KpiMetricsPage = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()
	const { tab } = useParams<{ tab?: string }>()
	const user = useAuthStore((s) => s.user)

	const filter = useKpiMetricsFilterStore((s) => s.filter)
	const setFilter = useKpiMetricsFilterStore((s) => s.setFilter)
	const { debouncedValue: debouncedPersonName } = useDebouncedValue(
		filter.personName,
	)

	const currentTab = tab as KpiMetricsTab | undefined
	const isVereAgirTab = currentTab === 'ver-e-agir'

	const params = useMemo<KpiMetricsParams>(() => {
		const p: KpiMetricsParams = {}
		if (filter.weekOfYear != null) {
			p.weekOfYear = filter.weekOfYear
			if (filter.year != null) p.year = filter.year
		} else {
			p.rangeStart = filter.period.range.start.toISOString().slice(0, 10)
			p.rangeEnd = filter.period.range.end.toISOString().slice(0, 10)
		}
		if (filter.branchIds.length > 0) p.branchIds = filter.branchIds.join(',')
		if (filter.crIds.length > 0) p.crIds = filter.crIds.join(',')
		if (filter.jobGroupClusterId) p.jobGroupClusterId = filter.jobGroupClusterId
		else if (filter.jobGroupId) p.jobGroupId = filter.jobGroupId
		if (debouncedPersonName.trim()) p.personName = debouncedPersonName.trim()
		if (isVereAgirTab && filter.customerClassificationId)
			p.customerClassificationId = filter.customerClassificationId
		return p
	}, [filter, debouncedPersonName, isVereAgirTab])
	const { data, isLoading } = useKpiMetrics(
		params,
		!currentTab || !OWN_FETCH_TABS.includes(currentTab),
	)

	const badges: {
		tab: KpiMetricsTab
		title: string
		description: string
		icon: React.ReactNode
		iconBg: string
		borderColor: string
	}[] = [
		{
			tab: 'ver-e-agir',
			title: t('kpiMetrics.badges.vereAgir'),
			description: t('kpiMetrics.badges.vereAgirDescription'),
			icon: <Eye className="size-5 text-kaefer-forest-green" />,
			iconBg: 'bg-kaefer-forest-green/15',
			borderColor: 'border-l-kaefer-forest-green',
		},
		{
			tab: 'acoes-consolidadas',
			title: t('kpiMetrics.badges.acoesConsolidadas'),
			description: t('kpiMetrics.badges.acoesConsolidadasDescription'),
			icon: <ListChecks className="size-5 text-kaefer-medium-grey" />,
			iconBg: 'bg-kaefer-medium-grey/15',
			borderColor: 'border-l-kaefer-medium-grey',
		},
		{
			tab: 'interacao',
			title: t('kpiMetrics.badges.interacao'),
			description: t('kpiMetrics.badges.interacaoDescription'),
			icon: <MessageCircle className="size-5 text-kaefer-violet" />,
			iconBg: 'bg-kaefer-violet/15',
			borderColor: 'border-l-kaefer-violet',
		},
		{
			tab: 'calibracao',
			title: t('kpiMetrics.badges.calibracao'),
			description: t('kpiMetrics.badges.calibracaoDescription'),
			icon: <Scale className="size-5 text-kaefer-forest-green" />,
			iconBg: 'bg-kaefer-forest-green/15',
			borderColor: 'border-l-kaefer-forest-green',
		},
		{
			tab: 'direcionada',
			title: t('kpiMetrics.badges.direcionada'),
			description: t('kpiMetrics.badges.direcionadaDescription'),
			icon: <Send className="size-5 text-kaefer-raspberry-pink" />,
			iconBg: 'bg-kaefer-raspberry-pink/20',
			borderColor: 'border-l-kaefer-raspberry-pink',
		},
		{
			tab: 'resposta-livre',
			title: t('kpiMetrics.badges.freeText'),
			description: t('kpiMetrics.badges.freeTextDescription'),
			icon: <PenLine className="size-5 text-kaefer-violet" />,
			iconBg: 'bg-kaefer-violet/15',
			borderColor: 'border-l-kaefer-violet',
		},
		{
			tab: 'cartao-amarelo',
			title: t('kpiMetrics.badges.cartaoAmarelo'),
			description: t('kpiMetrics.badges.cartaoAmareloDescription'),
			icon: <Flag className="size-5 text-kaefer-honey-yellow" />,
			iconBg: 'bg-kaefer-honey-yellow/20',
			borderColor: 'border-l-kaefer-honey-yellow',
		},
		{
			tab: 'dds',
			title: t('kpiMetrics.badges.dds'),
			description: t('kpiMetrics.badges.ddsDescription'),
			icon: <CalendarCheck className="size-5 text-kaefer-sapphire-blue" />,
			iconBg: 'bg-kaefer-sapphire-blue/15',
			borderColor: 'border-l-kaefer-sapphire-blue',
		},
	]

	const visibleBadges = user
		? badges.filter((b) => hasKpiMetricsTabAccess(user.role, b.tab))
		: badges

	const canViewCurrentTab =
		!currentTab || !user || hasKpiMetricsTabAccess(user.role, currentTab)

	if (currentTab && !canViewCurrentTab) {
		navigate(ROUTES.ADMINISTRATION.KPI_METRICS, { replace: true })
		return null
	}

	const tabLabel = badges.find((b) => b.tab === currentTab)?.title

	return (
		<AdminCardPage
			icon={<Gauge className="size-5" />}
			title={tabLabel ?? t('kpiMetrics.title')}
			subtitle={t('kpiMetrics.subtitle')}
			onBack={() =>
				currentTab
					? navigate(ROUTES.ADMINISTRATION.KPI_METRICS)
					: navigate(ROUTES.DASHBOARD.HOME)
			}
		>
			<KpiMetricsFilterBar
				value={filter}
				onChange={setFilter}
				showCustomerClassification={isVereAgirTab}
			/>

			{!currentTab ? (
				<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
					{visibleBadges.map((badge) => (
						<CardButton
							key={badge.tab}
							icon={badge.icon}
							iconBg={badge.iconBg}
							borderColor={badge.borderColor}
							title={badge.title}
							description={badge.description}
							onClick={() =>
								navigate(
									ROUTES.ADMINISTRATION.KPI_METRICS_TAB.replace(
										':tab',
										badge.tab,
									),
								)
							}
						/>
					))}
				</div>
			) : currentTab === 'ver-e-agir' ? (
				<VerEAgirDashboard params={params} />
			) : currentTab === 'interacao' ? (
				<InteracaoDashboard params={params} />
			) : currentTab === 'direcionada' ? (
				<DirecionadaDashboard params={params} />
			) : currentTab === 'resposta-livre' ? (
				<FreeTextDashboard params={params} />
			) : currentTab === 'dds' ? (
				<DdsDashboard params={params} />
			) : isLoading || !data ? (
				<KpiMetricsDashboardLoading />
			) : (
				<KpiMetricsTabContent tab={currentTab} data={data} params={params} />
			)}
		</AdminCardPage>
	)
}

function KpiMetricsTabContent({
	tab,
	data,
	params,
}: {
	tab: Exclude<
		KpiMetricsTab,
		'ver-e-agir' | 'interacao' | 'direcionada' | 'resposta-livre' | 'dds'
	>
	data: NonNullable<ReturnType<typeof useKpiMetrics>['data']>
	params: KpiMetricsParams
}) {
	switch (tab) {
		case 'acoes-consolidadas':
			return <AcoesConsolidadasDashboard data={data} />
		case 'calibracao':
			return <CalibracaoDashboard data={data} params={params} />
		case 'cartao-amarelo':
			return <CartaoAmareloDashboard data={data} />
		default:
			return null
	}
}
